import * as Sentry from "@sentry/react";
import type { AxiosError, AxiosRequestConfig } from "axios";
import axios, { isAxiosError } from "axios";

import { getToken, logout as triggerLogout } from "utils/auth";
import { getRealtimeConnectionId } from "utils/realtimeConnection";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  export?: boolean;
}

const ApiClient = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}`,
  timeout: 30000,
});

ApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    const connectionId = getRealtimeConnectionId();
    if (connectionId) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>)["x-realtime-connection-id"] = connectionId;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

ApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;

    if (status === 401) {
      triggerLogout();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    Sentry.withScope((scope) => {
      scope.setTag("kind", "api_error");
      scope.setContext("api", {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status,
        responseData: error.response?.data,
      });
      Sentry.captureException(error);
    });

    return Promise.reject(error);
  },
);

const handleRequest = async <T, P = unknown>(
  method: "get" | "post" | "put" | "delete",
  url: string,
  payload?: P,
  config?: CustomAxiosRequestConfig,
): Promise<T> => {
  try {
    let response;
    if (method === "get" || method === "delete") {
      response = await ApiClient.request<T>({
        method,
        url,
        params: payload,
        ...config,
      });
    } else {
      response = await ApiClient.request<T>({
        method,
        url,
        data: payload,
        ...config,
      });
    }

    if (config?.export) {
      return response as T;
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) throw error;
    Sentry.captureException(error, { tags: { kind: "unexpected_api_error" } });
    throw error;
  }
};

export const api = {
  get: <T, P = unknown>(url: string, params?: P, config?: CustomAxiosRequestConfig) =>
    handleRequest<T, P>("get", url, params, config),
  post: <T, P = unknown>(url: string, data?: P, config?: CustomAxiosRequestConfig) =>
    handleRequest<T, P>("post", url, data, config),
  put: <T, P = unknown>(url: string, data?: P, config?: CustomAxiosRequestConfig) =>
    handleRequest<T, P>("put", url, data, config),
  delete: <T, P = unknown>(url: string, params?: P, config?: CustomAxiosRequestConfig) =>
    handleRequest<T, P>("delete", url, params, config),
};
